import { useEffect, useMemo, useState } from 'react';
import { Modal } from '@mantine/core';
import { Button } from './Button';
import { Icon } from './Icon';
import { PreviewRows } from './PreviewRows';
import type { PreviewOutput } from '@/api/imports';
import {
  inspectImportColumns,
  tryImportMapping,
  listImportMappings,
  listMappingTargets,
  saveImportMapping,
  TRANSFORMS,
  type AttributeTargets,
  type ColumnReport,
  type EventTarget,
  type FieldMapping,
  type MappingTarget,
  type MappingView,
} from '@/api/mappings';

type Props = {
  importId: number;
  sheet?: string;
  /** Appelé quand un profil est enregistré : le parent relance l'aperçu avec. */
  onSaved: (mapping: MappingView) => void;
  onCancel?: () => void;
};

const REQUIRED_TARGETS = ['subject.phoneNumber', 'occurredAt'];

/**
 * Écran de correspondance des colonnes.
 *
 * Il rend au métier ce qui était jusqu'ici du code : l'opérateur voit les
 * colonnes réelles du fichier avec leurs valeurs, les associe aux champs
 * canoniques, et enregistre le tout comme un profil réutilisable. Le fichier
 * suivant du même partenaire n'exigera plus rien.
 *
 * Trois partis pris.
 *
 * Les colonnes de gauche montrent des VALEURS et un taux de remplissage, pas
 * seulement des en-têtes : c'est ce qui permet de reconnaître une colonne mal
 * nommée, ou de repérer qu'elle est vide neuf fois sur dix avant de la mapper.
 *
 * Un profil peut déclarer PLUSIEURS événements par ligne, parce que les
 * fichiers réels le font — un profil marchand porte un enrôlement et un résumé
 * d'usage sur la même ligne.
 *
 * La clé d'idempotence est choisie explicitement. C'est elle qui décide si un
 * ré-import est neutre ou s'il duplique ; la deviner serait reproduire le
 * défaut qu'on vient de corriger dans les connecteurs écrits à la main.
 */
export function MappingEditor({ importId, sheet, onSaved, onCancel }: Props) {
  const [report, setReport] = useState<ColumnReport | null>(null);
  const [types, setTypes] = useState<AttributeTargets[]>([]);
  const [envelope, setEnvelope] = useState<MappingTarget[]>([]);
  const [name, setName] = useState('');
  const [targets, setTargets] = useState<EventTarget[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // Essai : le résultat d'une correspondance non enregistrée. C'est ce qui
  // permet d'ajuster sans créer une version de profil à chaque tâtonnement.
  const [trial, setTrial] = useState<PreviewOutput | null>(null);
  const [trying, setTrying] = useState(false);
  // Enregistrer crée une version de profil conservée définitivement et
  // l'attache à l'import : ça se confirme. Essayer n'écrit rien : ça ne se
  // confirme pas.
  const [confirmSave, setConfirmSave] = useState(false);
  const [existing, setExisting] = useState<MappingView[]>([]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [cols, cat, saved] = await Promise.all([
          inspectImportColumns(importId, sheet),
          listMappingTargets(),
          listImportMappings(),
        ]);
        if (cancelled) return;
        setReport(cols);
        setTypes(cat.types);
        setEnvelope(cat.envelope);
        setExisting(saved);
        if (targets.length === 0 && cat.types.length > 0) {
          setTargets([emptyTarget(cat.types[0])]);
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [importId, sheet]);

  const columns = report?.columns ?? [];
  const mappedColumns = useMemo(() => {
    const set = new Set<string>();
    for (const t of targets) {
      for (const f of t.fields) {
        if (f.column && f.transform !== 'ignore') set.add(f.column);
      }
    }
    return set;
  }, [targets]);

  function targetsFor(attributeType: string): MappingTarget[] {
    const type = types.find((t) => t.code === attributeType);
    return [...envelope, ...(type?.targets ?? [])];
  }

  // Changer le type d'événement doit réviser le système d'origine : chaque type
  // n'autorise qu'une liste de sources. Sans ça, l'ancienne valeur restait dans
  // l'état alors qu'elle ne figurait plus parmi les options — le navigateur
  // affichait donc la première option de la liste pendant que le code envoyait
  // l'ancienne. L'écran affichait « ORANGE » et l'API rejetait
  // « CRYSTAL_BACKOFFICE ».
  function changeAttributeType(index: number, attributeType: string) {
    const allowed = types.find((x) => x.code === attributeType)?.allowedSources ?? [];
    setTargets((prev) =>
      prev.map((t, i) => {
        if (i !== index) return t;
        const source = allowed.includes(t.sourceSystem) ? t.sourceSystem : (allowed[0] ?? 'MANUAL');
        return { ...t, attributeType, sourceSystem: source };
      }),
    );
  }

  function updateTarget(index: number, patch: Partial<EventTarget>) {
    setTargets((prev) => prev.map((t, i) => (i === index ? { ...t, ...patch } : t)));
  }

  function updateField(ti: number, fi: number, patch: Partial<FieldMapping>) {
    setTargets((prev) =>
      prev.map((t, i) =>
        i === ti ? { ...t, fields: t.fields.map((f, j) => (j === fi ? { ...f, ...patch } : f)) } : t,
      ),
    );
  }

  // Proposition par défaut : le pivot et la date de survenance identifient une
  // ligne dans l'immense majorité des cas. L'opérateur peut affiner, mais il ne
  // part pas d'une page blanche sur une notion technique.
  useEffect(() => {
    setTargets((prev) =>
      prev.map((t) => {
        if (t.idempotencyKey.length > 0) return t;
        const mapped = t.fields.filter((f) => f.target && f.transform !== 'ignore').map((f) => f.target);
        const defaults = REQUIRED_TARGETS.filter((r) => mapped.includes(r));
        return defaults.length === REQUIRED_TARGETS.length ? { ...t, idempotencyKey: defaults } : t;
      }),
    );
  }, [targets.map((t) => t.fields.map((f) => f.target).join(',')).join('|')]);

  function addField(ti: number, column?: string) {
    setTargets((prev) =>
      prev.map((t, i) =>
        i === ti ? { ...t, fields: [...t.fields, { target: '', column, transform: 'text' }] } : t,
      ),
    );
  }

  function removeField(ti: number, fi: number) {
    setTargets((prev) =>
      prev.map((t, i) => (i === ti ? { ...t, fields: t.fields.filter((_, j) => j !== fi) } : t)),
    );
  }

  function missingRequired(t: EventTarget): string[] {
    const filled = new Set(t.fields.filter((f) => f.transform !== 'ignore').map((f) => f.target));
    return REQUIRED_TARGETS.filter((r) => !filled.has(r));
  }

  async function tryOut() {
    setError(null);
    setTrial(null);
    setTrying(true);
    try {
      setTrial(await tryImportMapping({ importId, sheet, definition: { region: 'CI', targets } }));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setTrying(false);
    }
  }

  // Version qui sera créée : la suivante si le nom existe déjà, la première
  // sinon. L'opérateur doit savoir s'il crée un profil ou s'il en fait évoluer
  // un que d'autres imports référencent peut-être.
  const nextVersion = (existing.find((m) => m.name === name.trim())?.version ?? 0) + 1;

  async function save() {
    setConfirmSave(false);
    setError(null);
    if (!name.trim()) {
      setError('Donnez un nom au profil : c’est lui qui le rendra réutilisable au prochain lot.');
      return;
    }
    setSaving(true);
    try {
      const mapping = await saveImportMapping({
        name: name.trim(),
        headerSignature: report?.headerSignature,
        definition: { region: 'CI', targets },
      });
      onSaved(mapping);
    } catch (e) {
      // Le serveur revalide le profil : son message nomme précisément ce qui
      // manque, c'est plus utile qu'un « profil invalide ».
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  if (error && !report) {
    return <div className="err-msg">{error}</div>;
  }
  if (!report) {
    return <div className="muted" style={{ fontWeight: 600 }}>Lecture des colonnes…</div>;
  }

  return (
    <>
      <Modal
        opened={confirmSave}
        onClose={() => setConfirmSave(false)}
        size={520}
        centered
        title="Enregistrer ce profil ?"
      >
        <dl className="confirm-run">
          <dt>Profil</dt>
          <dd>{name.trim() || '—'}</dd>
          <dt>Version créée</dt>
          <dd>
            v{nextVersion}
            {nextVersion > 1 ? ' — les versions précédentes sont conservées' : ' — nouveau profil'}
          </dd>
        </dl>
        <p style={{ fontSize: 13, color: 'var(--ink-2)', margin: '0 0 6px' }}>
          Le profil sera enregistré, appliqué à cet import, et un aperçu sera relancé avec.
        </p>
        <p className="muted" style={{ fontSize: 12.5, fontWeight: 600, margin: 0 }}>
          Aucune donnée n'est importée à cette étape. L'import ne part qu'au lancement, qui reste
          à confirmer séparément.
        </p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 18 }}>
          <Button variant="ghost" onClick={() => setConfirmSave(false)}>Annuler</Button>
          <Button variant="primary" icon="check" onClick={() => void save()} loading={saving}>
            Enregistrer
          </Button>
        </div>
      </Modal>

    <div className="mapping-editor">
      <div className="mapping-cols">
        <div className="mapping-cols-head">
          <Icon name="database" size={15} />
          Colonnes du fichier
          <span className="muted" style={{ fontWeight: 600, fontSize: 11.5 }}>
            {columns.length} · {report.rowsScanned} lignes lues
          </span>
        </div>
        <div className="mapping-cols-list">
          {columns.map((c) => (
            <button
              key={c.index}
              type="button"
              className={mappedColumns.has(c.header) ? 'col-chip mapped' : 'col-chip'}
              title="Ajouter une correspondance pour cette colonne"
              onClick={() => addField(0, c.header)}
            >
              <span className="col-name">{c.header || `(colonne ${c.index + 1})`}</span>
              <span className="col-fill">{Math.round(c.fillRatio * 100)}%</span>
              <span className="col-sample">{(c.samples ?? []).slice(0, 2).join(' · ') || '—'}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="mapping-form">
        <label className="field">
          <span>Nom du profil</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Ex : Partenaire X — format août 2026"
          />
        </label>

        {targets.map((t, ti) => {
          const available = targetsFor(t.attributeType);
          const missing = missingRequired(t);
          const type = types.find((x) => x.code === t.attributeType);
          return (
            <div key={ti} className="mapping-target">
              <div className="mapping-target-head">
                <label className="head-field">
                  <span>Type d'événement produit</span>
                  <select
                    value={t.attributeType}
                    onChange={(e) => changeAttributeType(ti, e.target.value)}
                  >
                    {types.map((x) => (
                      <option key={x.code} value={x.code}>{x.label}</option>
                    ))}
                  </select>
                </label>
                <label className="head-field">
                  <span>Système d'origine</span>
                  <select
                    value={t.sourceSystem}
                    onChange={(e) => updateTarget(ti, { sourceSystem: e.target.value })}
                  >
                    {(type?.allowedSources ?? []).map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </label>
                <label className="once-toggle" title="Un attribut du marchand — son nom, sa date d'enrôlement — est répété sur chacune de ses lignes. Sans cette option, il produirait autant d'événements que de lignes.">
                  <input
                    type="checkbox"
                    checked={t.oncePerKey ?? false}
                    onChange={(e) => updateTarget(ti, { oncePerKey: e.target.checked })}
                  />
                  Une seule fois par marchand
                </label>
                {targets.length > 1 && (
                  <button
                    type="button"
                    className="btn ghost"
                    onClick={() => setTargets((p) => p.filter((_, i) => i !== ti))}
                  >
                    Retirer cet événement
                  </button>
                )}
              </div>

              <table className="mapping-table">
                <thead>
                  <tr>
                    <th>Champ cible</th>
                    <th>Colonne source</th>
                    <th>Transformation</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {t.fields.map((f, fi) => (
                    <tr key={fi}>
                      <td>
                        <select
                          value={f.target}
                          onChange={(e) => {
                            const suggested = available.find((a) => a.path === e.target.value);
                            updateField(ti, fi, {
                              target: e.target.value,
                              transform:
                                f.transform === 'constant' ? 'constant' : suggested?.transform ?? f.transform,
                            });
                          }}
                        >
                          <option value="">— choisir —</option>
                          {available.map((a) => (
                            <option key={a.path} value={a.path}>
                              {a.path}{a.required ? ' *' : ''}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        {f.transform === 'constant' ? (
                          <input
                            value={f.constant ?? ''}
                            placeholder="valeur fixe"
                            onChange={(e) => updateField(ti, fi, { constant: e.target.value })}
                          />
                        ) : (
                          <select
                            value={f.column ?? ''}
                            onChange={(e) => updateField(ti, fi, { column: e.target.value })}
                          >
                            <option value="">— colonne —</option>
                            {columns.map((c) => (
                              <option key={c.index} value={c.header}>{c.header}</option>
                            ))}
                          </select>
                        )}
                      </td>
                      <td>
                        <select
                          value={f.transform}
                          onChange={(e) => updateField(ti, fi, { transform: e.target.value })}
                        >
                          {TRANSFORMS.map((tr) => (
                            <option key={tr.value} value={tr.value}>{tr.label}</option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <button type="button" className="btn ghost" onClick={() => removeField(ti, fi)}>
                          <Icon name="x" size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="mapping-target-foot">
                <Button size="sm" icon="plus" onClick={() => addField(ti)}>
                  Ajouter une correspondance
                </Button>
              </div>

              {/* Ce qui identifie une ligne. Le choix est présenté en pastilles
                  plutôt qu'en liste à sélection multiple : une liste multiple
                  exige un Ctrl+clic que personne ne devine, et occupait le
                  tiers de l'écran pour trois valeurs. */}
              <div className="key-picker">
                <span className="key-picker-label">
                  Ce qui identifie une ligne
                  <span className="muted"> — évite les doublons si le fichier est réimporté</span>
                </span>
                <div className="key-chips">
                  {t.fields
                    .filter((f) => f.target && f.transform !== 'ignore')
                    .map((f) => {
                      const on = t.idempotencyKey.includes(f.target);
                      return (
                        <button
                          key={f.target}
                          type="button"
                          className={on ? 'key-chip on' : 'key-chip'}
                          onClick={() =>
                            updateTarget(ti, {
                              idempotencyKey: on
                                ? t.idempotencyKey.filter((k) => k !== f.target)
                                : [...t.idempotencyKey, f.target],
                            })
                          }
                        >
                          {on ? '✓ ' : ''}{f.target.replace('payload.', '').replace('subject.', '')}
                        </button>
                      );
                    })}
                  {t.fields.filter((f) => f.target && f.transform !== 'ignore').length === 0 && (
                    <span className="muted" style={{ fontSize: 12 }}>
                      Renseignez d'abord des correspondances ci-dessus.
                    </span>
                  )}
                </div>
              </div>

              {missing.length > 0 && (
                <div className="mapping-missing">
                  <Icon name="alert" size={14} />
                  Champs obligatoires non renseignés : {missing.join(', ')}
                </div>
              )}
            </div>
          );
        })}

        <Button
          size="sm"
          icon="plus"
          onClick={() => types.length > 0 && setTargets((p) => [...p, emptyTarget(types[0])])}
        >
          Ajouter un second événement pour la même ligne
        </Button>

        {trial && (
          <div className={trial.rejected > 0 ? 'trial-result warn' : 'trial-result'}>
            <div className="trial-head">
              <Icon name={trial.rejected > 0 ? 'alert' : 'check'} size={15} />
              Essai : {trial.accepted}/{trial.sampleSize} événements simulés sans rejet
              {trial.rejected > 0 ? ` · ${trial.rejected} rejet(s)` : ''}
              <span className="muted" style={{ marginLeft: 'auto', fontWeight: 600, fontSize: 11.5 }}>
                rien n'a été écrit, rien n'a été enregistré
              </span>
            </div>
            {Object.entries(trial.rejectionReasons ?? {}).map(([code, n]) => (
              <div key={code} className="trial-reason">
                <span className="mono">{code}</span> — {n} ligne(s)
                {trial.rows?.find((r) => r.code === code)?.message ? (
                  <span className="muted"> · {trial.rows.find((r) => r.code === code)?.message}</span>
                ) : null}
              </div>
            ))}
          </div>
        )}

        {trial && <PreviewRows preview={trial} />}

        {error && <div className="err-msg" style={{ marginTop: 14 }}>{error}</div>}

        <div style={{ display: 'flex', gap: 10, marginTop: 18, justifyContent: 'flex-end' }}>
          {onCancel && (
            <Button variant="ghost" onClick={onCancel} disabled={saving || trying}>Annuler</Button>
          )}
          <Button
            icon="play"
            onClick={() => void tryOut()}
            loading={trying}
            disabled={saving}
            title="Simulation : rien n'est écrit, rien n'est enregistré. Rejouable autant de fois que nécessaire."
          >
            {trying ? 'Essai…' : 'Essayer sans enregistrer'}
          </Button>
          <Button
            variant="primary"
            icon="check"
            onClick={() => (name.trim() ? setConfirmSave(true) : void save())}
            loading={saving}
          >
            {saving ? 'Enregistrement…' : 'Enregistrer et prévisualiser'}
          </Button>
        </div>
      </div>
    </div>
    </>
  );
}

function emptyTarget(type: AttributeTargets): EventTarget {
  return {
    attributeType: type.code,
    sourceSystem: type.allowedSources?.[0] ?? 'MANUAL',
    fields: [],
    idempotencyKey: [],
  };
}
